import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { IPaginatedResponse } from "../models/Pagination";
import {
  ICreatePurchaseAwb,
  ICreatePurchaseOrder,
  ICreatePurchaseReceipt,
  IPurchaseAwb,
  IPurchaseFilter,
  IPurchaseOrder,
  IPurchaseOrderDetail,
  IPurchaseOrderOverview,
  IPurchaseReceipt,
  IPurchaseRequest,
  IPurchaseRequestDetail,
  IPurchaseSupplier,
  IUpdatePurchaseOrder,
} from "../models/Purchase";
import { purchaseService } from "../services/purchaseService";

export function useGetPurchaseRequest(
  params: IPurchaseFilter,
): UseQueryResult<IPaginatedResponse<IPurchaseRequest>> {
  return useQuery({
    queryKey: ["purchase-requests", params],
    queryFn: () => purchaseService.getAllPurchases(params),
  });
}

export function useGetPurchaseRequestByAlias(
  alias: string | undefined,
): UseQueryResult<IPurchaseRequestDetail> {
  return useQuery({
    queryKey: ["purchase-requests-detail", alias],
    queryFn: () => purchaseService.getPurchaseByAlias(alias!),
    enabled: !!alias,
  });
}

export function useGetPurchaseOrderAssignment(
  params: IPurchaseFilter,
): UseQueryResult<IPaginatedResponse<IPurchaseRequest>> {
  return useQuery({
    queryKey: ["purchase-requests-assignment", params],
    queryFn: () => purchaseService.getPurchaseRequestAssignments(params),
  });
}

export function useApprovePurchaseRequestByPurchase(): UseMutationResult<
  IPurchaseRequest,
  unknown,
  number,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => purchaseService.approvePurchaseRequest(id!),
    onSuccess: (data) => {
      queryClient.setQueryData(
        ["purchase-requests-detail", data.requestNumber],
        (oldData: IPurchaseRequestDetail | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            status: data.status,
          };
        },
      );
    },
  });
}

export function useAssignPurchaseOrder(): UseMutationResult<
  IPurchaseRequestDetail,
  unknown,
  { id: number; assigneeId: string },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, assigneeId }: { id: number; assigneeId: string }) =>
      purchaseService.assignPurchaseRequest(id!, assigneeId),
    onSuccess: (data) => {
      queryClient.setQueryData(
        ["purchase-requests-detail", data.requestNumber],
        (oldData: IPurchaseRequestDetail | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            assigneeId: data.assigneeId,
          };
        },
      );
    },
  });
}
export function useGetPurchaseOrderOverview(): UseQueryResult<IPurchaseOrderOverview> {
  return useQuery({
    queryKey: ["purchase-requests-overview"],
    queryFn: () => purchaseService.getPurchaseRequestOverview(),
  });
}
export function useCreatePurchaseReceipt(): UseMutationResult<
  IPurchaseReceipt,
  unknown,
  ICreatePurchaseReceipt,
  unknown
> {
  return useMutation({
    mutationFn: (data: ICreatePurchaseReceipt) =>
      purchaseService.createPurchaseReceipt(data),
  });
}
export function useGetPurchaseReceipt(
  params: IPurchaseFilter,
): UseQueryResult<IPaginatedResponse<IPurchaseReceipt>> {
  return useQuery({
    queryKey: ["purchase-receipts", params],
    queryFn: () => purchaseService.getPurchaseReceipts(params),
  });
}
export function useCreatePurchaseOrder(): UseMutationResult<
  IPurchaseOrder,
  unknown,
  ICreatePurchaseOrder,
  unknown
> {
  return useMutation({
    mutationFn: (data: ICreatePurchaseOrder) =>
      purchaseService.createPurchaseOrder(data),
  });
}
export function useGetPurchaseOrder(
  params: IPurchaseFilter,
): UseQueryResult<IPaginatedResponse<IPurchaseOrder>> {
  return useQuery({
    queryKey: ["purchase-orders", params],
    queryFn: () => purchaseService.getPurchaseOrder(params),
  });
}
export function useGetPurchaseOrderByPoNumber(
  alias: string | undefined,
): UseQueryResult<IPurchaseOrderDetail> {
  return useQuery({
    queryKey: ["purchase-orders-detail", alias],
    queryFn: () => purchaseService.getPurchaseOrderByPoNumber(alias!),
    enabled: !!alias,
  });
}
export function useGetPurchaseOrderByPurchaseRequest(
  purchaseRequestId: number | undefined,
): UseQueryResult<IPurchaseOrder[]> {
  return useQuery({
    queryKey: ["purchase-order-purchase-request", purchaseRequestId],
    queryFn: () =>
      purchaseService.getPurchaseOrderByPurchaseRequestId(purchaseRequestId!),
    enabled: !!purchaseRequestId,
  });
}

export function useUpdatePurchaseOrder(): UseMutationResult<
  IPurchaseOrderDetail,
  unknown,
  { id: number; data: IUpdatePurchaseOrder },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { id: number; data: IUpdatePurchaseOrder }) =>
      purchaseService.updatePurchaseOrder(body.id, body.data),
    onSuccess: (data) => {
      queryClient.setQueryData(
        ["purchase-orders-detail", data.pONumber],
        (oldData: IPurchaseOrderDetail | undefined) => {
          if (!oldData) return oldData;
          return data;
        },
      );
    },
  });
}
export function useSendPurchaseOrder(): UseMutationResult<
  {
    pONumber: string;
    status: string;
  },
  unknown,
  number,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => purchaseService.sendPurchaseOrder(id!),
    onSuccess: (data) => {
      queryClient.setQueryData(
        ["purchase-orders-detail", data.pONumber],
        (oldData: IPurchaseOrderDetail | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            status: data.status,
          };
        },
      );
    },
  });
}
export function useCreatePurchaseAwb(): UseMutationResult<
  IPurchaseAwb,
  unknown,
  ICreatePurchaseAwb,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ICreatePurchaseAwb) =>
      purchaseService.createPurchaseAwb(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["purchase-awb", data.purchaseOrderId],
      });
    },
  });
}
export function useGetPurchaseAwbByPurchaseOrderId(
  purchaseOrderId: number | undefined,
): UseQueryResult<IPurchaseAwb> {
  return useQuery({
    queryKey: ["purchase-awb", purchaseOrderId],
    queryFn: () =>
      purchaseService.getPurchaseAwbByPurchaseOrderId(purchaseOrderId!),
    enabled: !!purchaseOrderId,
  });
}

export function useGetSuppliers(
  params: IPurchaseFilter,
): UseQueryResult<IPaginatedResponse<IPurchaseSupplier>> {
  return useQuery({
    queryKey: ["suppliers", params],
    queryFn: () => purchaseService.getPurchaseSupplier(params),
  });
}

export function useCreateSupplier(): UseMutationResult<
  IPurchaseSupplier,
  unknown,
  Omit<IPurchaseSupplier, "id">,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<IPurchaseSupplier, "id">) =>
      purchaseService.createSupplier(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
    },
  });
}

export function useUpdateSupplier(): UseMutationResult<
  IPurchaseSupplier,
  unknown,
  { id: number; data: Omit<IPurchaseSupplier, "id">; query: IPurchaseFilter },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      id: number;
      data: Omit<IPurchaseSupplier, "id">;
      query: IPurchaseFilter;
    }) => purchaseService.updateSupplier(body.id, body.data),
    onSuccess: (data, variables) => {
      queryClient.setQueryData<IPaginatedResponse<IPurchaseSupplier>>(
        ["suppliers", variables.query],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: oldData.data.map((item) =>
              item.id === data.id ? data : item,
            ),
          };
        },
      );
    },
  });
}

export function useDeleteSupplier(): UseMutationResult<
  void,
  unknown,
  { id: number; query: IPurchaseFilter },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { id: number; query: IPurchaseFilter }) =>
      purchaseService.deleteSupplier(body.id),
    onSuccess: (_, variable) => {
      queryClient.setQueryData<IPaginatedResponse<IPurchaseSupplier>>(
        ["suppliers", variable.query],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: oldData.data.filter((item) => item.id !== variable.id),
          };
        },
      );
    },
  });
}
